import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ApiService } from '../../services/api/api.service';
import { FormatService } from '../../services/format/format.service';
import * as moment from 'moment';

// Read-only detail of one USER's Tahfidz: hero + stat strip, then tabs
// (their targets with an expandable per-surah/ayah view, the full
// memorization map per surah, and their activity). Muraja'ah has its own
// detail page (pages/murojaah-detail).
@Component({
  selector: 'app-hafalan-detail',
  templateUrl: './hafalan-detail.component.html',
  styleUrls: ['./hafalan-detail.component.css']
})
export class HafalanDetailComponent implements OnInit, OnDestroy {
  id : number = 0;
  user : any = null;
  isHeaderLoading : boolean = true;
  notFound : boolean = false;
  directoryFile : string = '';
  pageSize : number = 10;

  tab : string = 'targets';
  readonly tabs = [
    {key: 'targets', label: 'Target'},
    {key: 'map', label: 'Peta Hafalan'},
    {key: 'activity', label: 'Aktivitas'},
  ];
  loading : any = {};
  private loaded : any = {};

  targets : any = [];
  targetFilter : string = '';
  expanded : any = {};   // target id -> {loading, surahs}
  map : any = [];
  act : any = [];
  actTotal : number = 0;
  actPage : number = 1;
  actPages : any = [];

  isViewer : string = '';
  private routeSub : Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
  ) { }

  ngOnInit(): void {
    moment.locale('id');
    this.pageSize = this.apiService.LIMIT || 10;
    this.routeSub = this.route.paramMap.subscribe(params => {
      this.id = Number(params.get('id')) || 0;
      this.user = null; this.notFound = false; this.isHeaderLoading = true;
      this.targets = []; this.expanded = {}; this.map = []; this.act = []; this.actTotal = 0; this.actPage = 1; this.loaded = {};
      this.loadHeader();
    });
  }
  ngOnDestroy(): void {
    if(this.routeSub){ this.routeSub.unsubscribe(); }
    document.body.style.overflow = '';
  }

  loadHeader(){
    this.apiService.methodGetSilent(`hafalan/user/${this.id}`, this.authService.getToken()).subscribe((response) => {
      this.isHeaderLoading = false;
      if(response && response.status == 200 && response.data){
        this.directoryFile = response.directoryFile || '';
        const u = response.data;
        u.nameVw = u.name || 'Akun tidak ditemukan';
        u.photoUrl = this.photoUrl(u.photo, 'user/avatar/');
        u.firstTargetVw = u.first_target_at ? moment(u.first_target_at).format('D MMMM YYYY') : '—';
        u.lastProgressVw = u.last_progress_at ? moment(u.last_progress_at).fromNow() : 'Belum ada';
        u.lastProgressFull = u.last_progress_at ? moment(u.last_progress_at).format('D MMMM YYYY HH:mm') : '';
        u.lastSignVw = u.last_sign ? moment(u.last_sign).fromNow() : '—';
        u.quranPct = u.ayat_hafal ? Math.max(0.1, Math.round(u.ayat_hafal / 6236 * 1000) / 10) : 0;
        u.quranPctVw = String(u.quranPct).replace('.', ',');
        u.completePct = u.targets_total ? Math.round(u.targets_complete / u.targets_total * 100) : 0;
        this.user = u;
        this.openTab(this.tab, true);
      }else{
        this.notFound = true;
        if(response && response.message){ this.toastr.error(response.message, 'Gagal memuat pengguna'); }
      }
    }, () => { this.isHeaderLoading = false; this.notFound = true; });
  }

  photoUrl(photo : any, folder : string){
    if(!photo){ return ''; }
    return String(photo).includes('http') ? photo : `${this.directoryFile}${folder}${photo}`;
  }
  dayLabel(day : any){
    if(day == null){ return ''; }
    return moment.utc(Number(day) * 86400000).format('D MMM YYYY');
  }
  statusLabel(s : string){
    if(s === 'complete'){ return 'Selesai'; }
    if(s === 'overdue'){ return 'Lewat tenggat'; }
    return 'Berjalan';
  }
  back(){ this.router.navigate(['/hafalan']); }

  // ===== tabs =====
  openTab(key : string, force : boolean = false){
    this.tab = key;
    if(!force){ this.scrollTabsToTop(); }
    if(this.loaded[key] && !force){ return; }
    this.loaded[key] = true;
    switch(key){
      case 'targets': return this.loadTargets();
      case 'map': return this.loadMap();
      case 'activity': return this.loadActivity();
    }
  }
  private scrollTabsToTop(){
    const tabs : any = document.querySelector('.td-tabs');
    if(!tabs){ return; }
    const card : any = tabs.closest('.card') || tabs;
    const topbar : any = document.querySelector('.topbar');
    const pos = topbar ? getComputedStyle(topbar).position : '';
    const offset = ((pos === 'fixed' || pos === 'sticky') ? topbar.offsetHeight : 0) + 12;
    const top = card.getBoundingClientRect().top + window.pageYOffset - offset;
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth'});
  }

  // ----- targets -----
  loadTargets(){
    this.loading.targets = true;
    this.apiService.methodGetSilent(`hafalan/user/${this.id}/targets`, this.authService.getToken()).subscribe((response) => {
      this.loading.targets = false;
      if(response && response.status == 200){
        this.targets = (response.data || []).map((v : any) => {
          v.createdVw = this.dayLabel(v.created_day) || (v.created_at ? moment(v.created_at).format('D MMM YYYY') : '-');
          v.targetVw = this.dayLabel(v.target_day);
          v.deadlineVw = v.target_day == null ? 'Tanpa tenggat'
            : v.remaining_days > 0 ? `${this.formatNumber(v.remaining_days)} hari lagi`
            : v.remaining_days === 0 ? 'Hari ini'
            : `Lewat ${this.formatNumber(-v.remaining_days)} hari`;
          v.lastProgressVw = v.last_progress_at ? moment(v.last_progress_at).fromNow() : 'Belum ada';
          v.lastProgressFull = v.last_progress_at ? moment(v.last_progress_at).format('D MMMM YYYY HH:mm') : '';
          v.modeVw = v.mode == 1 ? 'Nafas' : 'Potongan';
          return v;
        });
      }
    }, () => { this.loading.targets = false; });
  }
  get visibleTargets(){
    if(!this.targetFilter){ return this.targets; }
    return this.targets.filter((t : any) => t.status === this.targetFilter);
  }
  targetCount(status : string){
    return status ? this.targets.filter((t : any) => t.status === status).length : this.targets.length;
  }
  // a target row expands into its per-surah breakdown + ayah map (lazy, cached)
  toggleTarget(v : any){
    const e = this.expanded[v.id] || (this.expanded[v.id] = {open: false, loading: false, surahs: null});
    e.open = !e.open;
    if(e.open && e.surahs === null && !e.loading){
      e.loading = true;
      this.apiService.methodGetSilent(`hafalan/${v.id}/surahs`, this.authService.getToken()).subscribe((response) => {
        e.loading = false;
        e.surahs = (response && response.status == 200) ? (response.data || []).map((s : any) => this.decorateSurah(s)) : [];
      }, () => { e.loading = false; e.surahs = []; });
    }
  }
  exp(v : any){ return this.expanded[v.id] || {open: false, loading: false, surahs: null}; }
  private decorateSurah(s : any){
    s.rangeVw = s.from === s.to ? `ayat ${s.from}` : `ayat ${s.from}–${s.to}`;
    s.lastVw = s.last_at ? moment(s.last_at).fromNow() : '—';
    (s.ayahs || []).forEach((a : any) => {
      a.title = `${s.name} ${a.ayah}` + (a.hafal ? ` — hafal ${moment(a.at).format('D MMM YYYY')}` : ' — belum');
    });
    return s;
  }

  // ----- memorization map -----
  loadMap(){
    this.loading.map = true;
    this.apiService.methodGetSilent(`hafalan/user/${this.id}/map`, this.authService.getToken()).subscribe((response) => {
      this.loading.map = false;
      if(response && response.status == 200){
        this.map = (response.data || []).map((s : any) => {
          s.lastVw = s.last_at ? moment(s.last_at).format('D MMM YYYY') : '—';
          (s.ayahs || []).forEach((a : any) => {
            a.title = `${s.name} ${a.ayah}` + (a.hafal ? ` — hafal ${moment(a.at).format('D MMM YYYY')}` : ' — belum');
          });
          return s;
        });
      }
    }, () => { this.loading.map = false; });
  }

  // ----- activity -----
  loadActivity(){
    this.loading.activity = true;
    const offset = (this.actPage - 1) * this.pageSize;
    this.apiService.methodGetSilent(`hafalan/user/${this.id}/activity/${offset}`, this.authService.getToken()).subscribe((response) => {
      this.loading.activity = false;
      if(response && response.status == 200){
        this.act = (response.data || []).map((a : any) => {
          a.surahName = a.surah_name || `Surat ${a.surah}`;
          a.rangeVw = a.ayah_from === a.ayah_to ? `ayat ${a.ayah_from}` : `ayat ${a.ayah_from}–${a.ayah_to}`;
          a.timeVw = a.at ? moment(a.at).fromNow() : '';
          a.timeFull = a.at ? moment(a.at).format('D MMMM YYYY HH:mm') : '';
          a.dayVw = a.at ? moment(a.at).format('D MMM YYYY, HH:mm') : '';
          return a;
        });
        this.actTotal = response.total || 0;
        this.actPages = this.pagesFor(this.actPage, this.pagesOf(this.actTotal));
      }
    }, () => { this.loading.activity = false; });
  }
  goActPage(p : any){
    if(p < 1 || p > this.pagesOf(this.actTotal) || p == this.actPage){ return; }
    this.actPage = p; this.loadActivity();
  }

  pagesOf(total : number){ return Math.max(1, Math.ceil(total / this.pageSize)); }
  pagesFor(current : number, total : number){
    const pages: any[] = [];
    for(let p = 1; p <= total; p++){
      if(p == 1 || p == total || Math.abs(p - current) <= 1){ pages.push(p); }
      else if(pages[pages.length - 1] != '...'){ pages.push('...'); }
    }
    return pages;
  }
  rangeFrom(page : number, total : number){ return total ? (page - 1) * this.pageSize + 1 : 0; }
  rangeTo(page : number, len : number){ return (page - 1) * this.pageSize + len; }
  formatNumber(value : any){ return this.formatService.dotFormat(value || 0); }

  openViewer(url : string){
    if(!url){ return; }
    this.isViewer = url;
    document.body.style.overflow = 'hidden';
  }
  closeViewer(){
    this.isViewer = '';
    document.body.style.overflow = '';
  }
  @HostListener('document:keydown.escape')
  onEscape(){ if(this.isViewer){ this.closeViewer(); } }
}
